import { FieldList, PlanBadge, Radio, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  UserAttributeDtoKind,
  UserAttributeKindByStringDtoValue,
} from '@hotjar/feedback/domain/common/UserAttributes/UserAttributesMapping';
import type { UserAttributeType } from '@hotjar/global-filters';
import type { UserAttributeDto } from '@hotjar/feedback/domain/common/UserAttributes/UserAttributeDto';
import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { usePlanForFeature } from '@hotjar/webapp/hooks/usePlanForFeature';
import { useAskFeature } from '@hotjar/feedback';
import { useTranslation, useTranslationWithPrefix } from '@hotjar/translations';
import type {
  AttributeTargetEntry,
  TargetEntry,
  UserTargetingMode,
} from '@hotjar/feedback/components/common/Wizard/common/targeting';
import {
  getTargetedUsers,
  isTargetingAllUsers,
} from '@hotjar/feedback/components/common/Wizard/common/targeting';
import type { AttributeTarget } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import { UserAttributeKind } from '@hotjar/webapp/domains/user-attributes/model';
import { UserAttributesTargetingRule } from './UserAttributesTargetingRule';
import { UserAttributesChooser } from './UserAttributesChooser';
import { useEditingCallbacks } from '../useEditingCallbacks';

const StyledRadio = styled(Radio)`
  margin-bottom: 0 !important;
`;

const StyledFieldList = styled(FieldList)`
  width: auto;
`;

const TargetingSeparator = styled.div`
  ${tokens.text('caption')}
  color: rgba(0,0,0,0.6);
`;

const SpecificUsers = styled.div`
  margin-left: ${tokens.spacer.x2};
  width: auto;
`;

const SelectedUserAttributes = styled.div`
  margin-top: ${tokens.spacer.x1};
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin-left: ${tokens.spacer.x2};
`;

const FEATURE_NAME = 'user_attributes.toggle';

const DefaultValues = {
  [UserAttributeKind.Number]: {
    rule_type: 'number',
    match_operation: 'exact',
    negate: false,
    pattern: '',
  },
  [UserAttributeKind.String]: {
    rule_type: 'string',
    match_operation: 'contains',
    negate: false,
    pattern: '',
  },
  [UserAttributeKind.Email]: {
    rule_type: 'string',
    match_operation: 'exact',
    negate: false,
    pattern: '',
  },
  [UserAttributeKind.Boolean]: { rule_type: 'boolean', match_operation: 'exact', pattern: 'true' },
  [UserAttributeKind.Date]: {
    rule_type: 'date',
    match_operation: 'exact_days_ago',
    pattern: 7,
  },
  [UserAttributeKind.Unknown]: {
    rule_type: 'string',
    match_operation: 'unknown',
    negate: false,
    pattern: true,
  },
} as const;

export interface UserAttributesTargetingProps {
  onTargetingChange: (targeting: TargetEntry[]) => void;
  targeting: TargetEntry[];
  userAttributes: UserAttributeDto[];
  onShowUpsellModal: (referrer: string) => void;
  entityType: 'survey' | 'widget' | 'invitation';
}

export function UserAttributesTargeting({
  onTargetingChange,
  targeting,
  userAttributes,
  onShowUpsellModal,
  entityType,
}: UserAttributesTargetingProps): JSX.Element {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.targeting.user-attribute');
  const { t: commonT } = useTranslation('common');
  const { onEditRule, onAddRule, onRemoveRule } = useEditingCallbacks(targeting, onTargetingChange);
  const entries = useMemo(() => getTargetedUsers(targeting), [targeting]);

  const userAttributeOptionMap = useMemo(() => {
    return new Map(
      userAttributes
        .filter((item) => item.rule_type !== UserAttributeDtoKind.UNKNOWN)
        .map((item) => {
          return [
            item.name,
            { kind: UserAttributeKindByStringDtoValue[item.rule_type], name: item.name },
          ];
        }),
    );
  }, [userAttributes]);

  const { data: site = {} as Site } = useCurrentSite();
  const canUseUserTargeting = useAskFeature(FEATURE_NAME, site);
  const planNeededForUserTargeting = usePlanForFeature(FEATURE_NAME);

  const handleUserAttributeSelected = useCallback(
    (newValue: UserAttributeType) => {
      onAddRule({
        name: newValue.name ?? '',
        component: 'attribute',
        ...DefaultValues[newValue.kind],
      } satisfies AttributeTarget);
    },
    [onAddRule],
  );

  const specificTargetingRef = useRef<AttributeTargetEntry[]>(
    entries !== undefined ? getTargetedUsers(entries) : [],
  );

  const [mode, setMode] = useState<UserTargetingMode>(
    isTargetingAllUsers(entries) ? 'all' : 'specific',
  );

  const onTargetingModeChange = useCallback(
    (newMode: UserTargetingMode) => {
      if (entries === undefined) return;
      const otherTargets = targeting.filter((entry) => entry.target.component !== 'attribute');
      setMode(newMode);
      if (newMode === 'all') {
        specificTargetingRef.current = entries;
        onTargetingChange(otherTargets);
        return;
      }

      onTargetingChange([...otherTargets, ...specificTargetingRef.current]);
    },
    [entries, targeting, onTargetingChange],
  );

  return (
    <>
      <StyledFieldList title={t('title')} helperText={t('description', { entityType })} size="m">
        <StyledRadio
          isChecked={mode === 'all'}
          id="user_targeting_all"
          name="USER_TARGETING_all}"
          onChange={() => {
            onTargetingModeChange('all');
          }}
          value="all"
          labelContent={t('options.all')}
        />
        <StyledRadio
          isChecked={mode === 'specific'}
          id="user_targeting_specific"
          name="USER_TARGETING_specific"
          onChange={() => {
            if (canUseUserTargeting) {
              onTargetingModeChange('specific');
            } else {
              onShowUpsellModal('polls.targeting.attributes');
            }
          }}
          value="specific"
          labelContent={
            <>
              {t('options.specific.title')}
              {!canUseUserTargeting && planNeededForUserTargeting && (
                <StyledPlanBadge kind={planNeededForUserTargeting} size="s" />
              )}
            </>
          }
        />
      </StyledFieldList>
      {mode === 'specific' && (
        <SpecificUsers>
          <StyledFieldList title={t('options.specific.description', { entityType })}>
            <div>
              <UserAttributesChooser
                onUserAttributeSelected={(a) => handleUserAttributeSelected(a)}
                userAttributeOptionIsLoading={false}
                userAttributeOptionList={Array.from(userAttributeOptionMap.values())}
              />
              {entries.length > 0 && (
                <SelectedUserAttributes>
                  {entries.map((rule, index) => {
                    return (
                      <Fragment key={rule.id}>
                        {index !== 0 && (
                          <TargetingSeparator>
                            {commonT('and').toLocaleUpperCase()}
                          </TargetingSeparator>
                        )}
                        <UserAttributesTargetingRule
                          rule={rule}
                          onEditRule={onEditRule}
                          onRemoveRule={onRemoveRule}
                        />
                      </Fragment>
                    );
                  })}
                </SelectedUserAttributes>
              )}
            </div>
          </StyledFieldList>
        </SpecificUsers>
      )}
    </>
  );
}
